/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 * 
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { ReactNode, useCallback, useContext, useEffect, useState, createContext } from "react";
import { useCookies } from "react-cookie";
import { useLocation } from "react-router-dom";

type ColorSchemeType = "dark" | "light"

type ContextType = {
  isModalOpen: boolean
  modalData: any
  openModal: (data: any) => void
  closeModal: () => void
  logoColor: string
  setLogoColor: (color: string) => void
  setColorScheme: (mode: ColorSchemeType) => void
  colorScheme: ColorSchemeType
}

const initialContent = {
  content: "",
};

const initialData: ContextType = {
  isModalOpen: false,
  modalData: initialContent,
  logoColor: "#000",
  openModal: (data: any) => { },
  closeModal: () => { },
  setLogoColor: (color: string) => { },
  setColorScheme: (mode: ColorSchemeType) => { },
  colorScheme: "light"
};

const cookieOptions = { maxAge: 2147483647, path: "/" }

const UIContext = createContext<ContextType>(initialData);

const UIContextProvider = ({ children }: { children: ReactNode }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(initialContent);
  const [logoColor, setLogoColor] = useState("#002750");
  const { pathname } = useLocation();
  const [colorSchemeCookie, setCookies] = useCookies(["color-scheme"])
  const [colorScheme, setColorScheme] = useState<ColorSchemeType>(() => {
    if (colorSchemeCookie["color-scheme"]) {
      return colorSchemeCookie["color-scheme"]
    } else if (window.matchMedia) {
      const userPreferedScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? "dark" : "light"
      setCookies("color-scheme", userPreferedScheme, cookieOptions)
      return userPreferedScheme
    } else {
      setCookies("color-scheme", "light", cookieOptions)
      return "light"
    }
  })
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (colorScheme !== colorSchemeCookie["color-scheme"]) {
      setCookies("color-scheme", colorScheme, cookieOptions)
    }
  }, [colorScheme])

  const handleEscape = (e: KeyboardEvent, closeFn: Function) => {
    if (e.key === "Escape") {
      closeFn();
    }
  };

  const handleEscapeModal = (e: KeyboardEvent) => {
    handleEscape(e, closeModal);
  };

  const openModal = useCallback((data: any) => {
    setModalData(data);
    setModalOpen(true);
    document.addEventListener("keydown", handleEscapeModal);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    document.removeEventListener("keydown", handleEscapeModal);
  }, []);

  const contextValue = {
    isModalOpen,
    modalData,
    openModal,
    closeModal,
    logoColor,
    setLogoColor,
    setColorScheme,
    colorScheme,
  };

  return (
    <UIContext.Provider value={contextValue}>{children}</UIContext.Provider>
  );
};

const useUI = () => useContext(UIContext);

export { UIContextProvider, useUI };
